import { useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { Card, CardHeader } from '../../../components/ui/Card';
import { Alert } from '../../../components/ui/Alert';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { SegmentedControl } from '../../../components/ui/Tabs';
import { ErrorState, LoadingState } from '../../../components/ui/States';
import { formatDayMonth } from '../../../utils/dateFormat';
import { useAuth } from '../../../hooks/useAuth';
import { useDismissRefreshItem, useRefreshList, useSendRefreshItem } from '../../../hooks/useContentRefresh';
import type { ContentRefreshItem, ContentRefreshKind } from '../../../api/content-refresh';

const SOURCE_LABEL: Record<ContentRefreshItem['sources'][number], string> = {
  gsc: 'Search Console',
  semrush: 'Competitors',
  peec: 'Peec',
};

/**
 * Marketing → Content → Opportunities: the Monday refresh list. Pages to
 * refresh and pages we're missing; each one is sent to the Notion Content
 * Engine or dismissed by hand. Nothing is sent on its own.
 */
export function RefreshList() {
  const list = useRefreshList();
  const [kind, setKind] = useState<ContentRefreshKind>('refresh');
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const canDecide = user?.role !== 'viewer';

  const items = list.data?.items ?? [];
  const shown = items.filter((i) => i.kind === kind);
  const counts = {
    refresh: items.filter((i) => i.kind === 'refresh').length,
    missing: items.filter((i) => i.kind === 'missing').length,
  };
  const week = list.data?.weekOf;

  return (
    <Card>
      <CardHeader
        title={week ? `Refresh list, week of ${formatDayMonth(new Date(`${week}T00:00:00`))}` : 'Refresh list'}
        description="Built each Monday from Search Console, competitor rankings (Semrush) and Peec. Send an item to the Content Engine as an Idea, or dismiss it. Nothing is sent or published on its own."
        action={
          <SegmentedControl
            ariaLabel="Refresh list kind"
            options={[
              { id: 'refresh', label: `Refresh (${counts.refresh})` },
              { id: 'missing', label: `Missing (${counts.missing})` },
            ]}
            value={kind}
            onChange={setKind}
          />
        }
      />
      {error && (
        <Alert variant="error" dismissible onClose={() => setError(null)} className="mb-3">
          {error}
        </Alert>
      )}
      {list.error ? (
        <ErrorState title="Couldn’t load the refresh list" error={list.error} onRetry={() => list.refetch()} />
      ) : list.isLoading ? (
        <LoadingState />
      ) : !week ? (
        <p className="text-body-sm text-text-tertiary">No list yet. The first one is built on Monday at 07:30.</p>
      ) : shown.length === 0 ? (
        <p className="text-body-sm text-text-tertiary">
          {kind === 'refresh' ? 'No pages to refresh this week.' : 'No missing pages this week.'}
        </p>
      ) : (
        <ul className="divide-y divide-border-light">
          {shown.map((item) => (
            <RefreshRow key={item.id} item={item} canDecide={canDecide} onError={setError} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function RefreshRow({
  item,
  canDecide,
  onError,
}: {
  item: ContentRefreshItem;
  canDecide: boolean;
  onError: (msg: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const send = useSendRefreshItem();
  const dismiss = useDismissRefreshItem();
  const busy = send.isPending || dismiss.isPending;
  const fail = (e: unknown) => onError(e instanceof Error ? e.message : 'Something went wrong.');
  const gsc = item.evidence.gsc;

  return (
    <li className="py-3">
      <div className="flex items-start gap-3">
        <button
          type="button"
          aria-label={open ? 'Hide details' : 'Show details'}
          onClick={() => setOpen((o) => !o)}
          className="mt-0.5 text-text-tertiary hover:text-text-primary"
        >
          {open ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-body-sm font-medium text-text-primary">{item.title}</span>
            {item.sources.map((s) => (
              <Badge key={s} variant="pill">{SOURCE_LABEL[s]}</Badge>
            ))}
          </div>
          {item.ourUrl && (
            <a href={item.ourUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-body-xs text-accent hover:underline">
              {item.ourUrl.replace('https://sequesto.com', '')}
              <ExternalLink className="h-3 w-3" aria-hidden />
            </a>
          )}
          {gsc && (
            <p className="text-body-xs text-text-tertiary tabular-nums">
              {gsc.impressions} impressions, position {gsc.position}, clicks {gsc.prevClicks} → {gsc.clicks} (28 days)
            </p>
          )}
        </div>
        <div className="flex flex-none items-center gap-2">
          {item.status === 'sent' ? (
            item.notionPageUrl ? (
              <a href={item.notionPageUrl} target="_blank" rel="noreferrer" className="text-body-xs text-accent hover:underline">
                In Notion
              </a>
            ) : (
              <Badge variant="success">Sent</Badge>
            )
          ) : item.status === 'dismissed' ? (
            <Badge>Dismissed</Badge>
          ) : canDecide ? (
            <>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => dismiss.mutate(item.id, { onError: fail })}>
                Dismiss
              </Button>
              <Button size="sm" variant="secondary" disabled={busy} onClick={() => send.mutate(item.id, { onError: fail })}>
                {send.isPending ? 'Sending…' : 'Send to Content Engine'}
              </Button>
            </>
          ) : null}
        </div>
      </div>
      {open && (
        <div className="ml-7 mt-2 space-y-2 text-body-xs text-text-secondary">
          <p className="whitespace-pre-line">{item.suggestion}</p>
          {gsc && gsc.queries.length > 0 && (
            <p>
              <span className="font-medium text-text-primary">Queries not yet won: </span>
              {gsc.queries.map((q) => `“${q.query}” (${q.impressions}, pos ${q.position})`).join(', ')}
            </p>
          )}
          {(item.evidence.competitors ?? []).map((c) => (
            <p key={`${c.domain}-${c.url}-${c.market}`}>
              <a href={c.url} target="_blank" rel="noreferrer" className="font-medium text-accent hover:underline">
                {c.domain}
              </a>{' '}
              <span className="text-text-tertiary">[{c.market.toUpperCase()}]</span>{' '}
              {c.keywords.slice(0, 6).map((k) => `“${k.keyword}” ${k.volume}/mo`).join(', ')}
            </p>
          ))}
          {(item.evidence.peec ?? []).flatMap((p) => p.referenceUrls).length > 0 && (
            <p>
              <span className="font-medium text-text-primary">Cited by AI instead of us: </span>
              {(item.evidence.peec ?? []).flatMap((p) => p.referenceUrls).slice(0, 4).join(', ')}
            </p>
          )}
        </div>
      )}
    </li>
  );
}
